/**
 * 通知状态管理模块
 */

// 从 localStorage 加载消息
const loadMessagesFromStorage = () => {
  try {
    const stored = localStorage.getItem('notification_messages')
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('加载消息失败:', e)
  }
  return []
}

// 保存消息到 localStorage（使用防抖优化性能）
let saveTimer = null
const saveMessagesToStorage = (messages) => {
  // 清除之前的定时器
  if (saveTimer) {
    clearTimeout(saveTimer)
  }
  
  // 防抖：500ms 内多次调用只执行最后一次
  saveTimer = setTimeout(() => {
    try {
      // 限制消息数量，避免存储过大
      const messagesToSave = messages.slice(0, 100)
      localStorage.setItem('notification_messages', JSON.stringify(messagesToSave))
    } catch (e) {
      // 如果存储失败（可能超出限制），尝试清理旧消息
      console.error('保存消息失败，尝试清理旧消息:', e)
      try {
        // 只保留最近 50 条
        const recentMessages = messages.slice(0, 50)
        localStorage.setItem('notification_messages', JSON.stringify(recentMessages))
      } catch (e2) {
        console.error('清理后保存仍然失败:', e2)
      }
    }
  }, 500) // 500ms 防抖
}

// 清理过期消息（7天前）
const cleanExpiredMessages = (messages) => {
  const now = Date.now()
  const sevenDays = 7 * 24 * 60 * 60 * 1000
  
  return messages.filter(message => {
    const messageTime = message.timestamp || message.newtime
    if (!messageTime) return true // 没有时间戳的消息保留
    
    const time = typeof messageTime === 'string' 
      ? new Date(messageTime).getTime() 
      : messageTime
    
    if (isNaN(time)) return true // 无效时间保留
    
    return (now - time) < sevenDays
  })
}

// 初始化消息数据
const initialMessagesRaw = loadMessagesFromStorage()
// 清理过期消息
const initialMessages = cleanExpiredMessages(initialMessagesRaw)
// 如果清理了消息，更新存储
if (initialMessages.length !== initialMessagesRaw.length) {
  try {
    localStorage.setItem('notification_messages', JSON.stringify(initialMessages))
  } catch (e) {
    // 忽略错误
  }
}
const initialUnreadCount = initialMessages.filter(m => !m.read).length
const initialLastMessage = initialMessages.length > 0 ? initialMessages[0] : null

export default {
  namespaced: true,
  state: {
    unreadCount: initialUnreadCount, // 未读消息数量（从加载的消息中计算）
    messages: initialMessages, // 消息列表（从 localStorage 加载）
    isConnected: false, // SSE 连接状态
    lastMessage: initialLastMessage // 最后一条消息
  },
  mutations: {
    // 设置连接状态
    SET_CONNECTION_STATUS(state, status) {
      state.isConnected = status
    },
    // 添加新消息
    ADD_MESSAGE(state, message) {
      state.messages.unshift(message)
      state.unreadCount++
      state.lastMessage = message
      // 限制消息列表长度，最多保留100条
      if (state.messages.length > 100) {
        state.messages = state.messages.slice(0, 100)
      }
      // 定期清理过期消息（每添加10条消息清理一次）
      if (state.messages.length % 10 === 0) {
        const cleaned = cleanExpiredMessages(state.messages)
        if (cleaned.length !== state.messages.length) {
          state.messages = cleaned
          // 重新计算未读数量
          state.unreadCount = cleaned.filter(m => !m.read).length
        }
      }
      // 持久化到 localStorage（防抖写入）
      saveMessagesToStorage(state.messages)
    },
    // 标记消息为已读
    MARK_AS_READ(state, messageId) {
      if (messageId) {
        const message = state.messages.find(m => m.id === messageId)
        if (message && !message.read) {
          message.read = true
          state.unreadCount = Math.max(0, state.unreadCount - 1)
          // 持久化到 localStorage
          saveMessagesToStorage(state.messages)
        }
      } else {
        // 如果没有指定ID，标记所有消息为已读
        state.messages.forEach(m => {
          if (!m.read) {
            m.read = true
          }
        })
        state.unreadCount = 0
        // 持久化到 localStorage
        saveMessagesToStorage(state.messages)
      }
    },
    // 清除所有消息
    CLEAR_MESSAGES(state) {
      state.messages = []
      state.unreadCount = 0
      state.lastMessage = null
      // 清除 localStorage
      try {
        localStorage.removeItem('notification_messages')
      } catch (e) {
        console.error('清除消息失败:', e)
      }
    },
    // 设置未读数量
    SET_UNREAD_COUNT(state, count) {
      state.unreadCount = Math.max(0, count)
    }
  },
  actions: {
    // 添加新消息
    addMessage({ commit, state }, message) {
      // 确保消息 ID 唯一
      let messageId = message.id
      if (!messageId) {
        // 如果没有 ID，生成一个基于时间戳和随机数的唯一 ID
        messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      } else {
        // 如果已有 ID，检查是否已存在，如果存在则生成新 ID
        const existingMessage = state.messages.find(m => m.id === messageId)
        if (existingMessage) {
          messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }
      }
      
      commit('ADD_MESSAGE', {
        ...message,
        id: messageId,
        read: false,
        timestamp: message.newtime || Date.now()
      })
    },
    // 标记为已读
    markAsRead({ commit }, messageId) {
      commit('MARK_AS_READ', messageId)
    },
    // 清除所有消息
    clearMessages({ commit }) {
      commit('CLEAR_MESSAGES')
    },
    // 设置连接状态
    setConnectionStatus({ commit }, status) {
      commit('SET_CONNECTION_STATUS', status)
    }
  },
  getters: {
    // 获取未读消息数量（优化：缓存计算结果）
    unreadCount: state => {
      // 直接返回 state 中的值，避免每次都重新计算
      // 只有在必要时才重新计算（比如在 ADD_MESSAGE 中）
      return state.unreadCount
    },
    // 获取所有消息
    messages: state => state.messages,
    // 获取未读消息
    unreadMessages: state => state.messages.filter(m => !m.read),
    // 获取最后一条消息
    lastMessage: state => state.lastMessage,
    // 获取连接状态
    isConnected: state => state.isConnected
  }
}

